import { colors } from "../../constants/colors";
import { Box, Typography } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import Link from "@mui/material/Link";
import { Feedback as FeedbackIcon } from "@mui/icons-material";
import WorkflowDiagram from "./WorkflowDiagram";
import { styles, FONT_SIZES } from "./TutorialContent.styles";
import ShortIntro from "../common/ShortIntro";

const TutorialContent = () => {
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={styles.title}>
        Documentation
      </Typography>

      <Box sx={styles.container}>
        <Box>
          <Typography variant="h5" gutterBottom sx={styles.subtitle}>
            Overview
          </Typography>
          <Box>
            <ShortIntro title="Name Pilot" styles={styles} />

            <Typography paragraph sx={styles.paragraph}>
              You can run in 3 modes or run a full report:
            </Typography>
            <Box component="ol" sx={{ pl: 2 }}>
              <Typography component="li" sx={styles.listItem}>
                <strong>Name Ideation</strong>: Generate memorable &
                market-ready brand names and score them across sevelar metrics
                to make informed decisions.
              </Typography>
              <Typography component="li" sx={styles.listItem}>
                <strong>Trademark Research</strong>: Check for potential
                trademark conflicts and score them across several metrics to
                estimate trademark infringement risk.
              </Typography>
              <Typography component="li" sx={styles.listItem}>
                <strong>Domain Research</strong>: Find domain name ideas and
                scores domain name availability, SEO, clarity, brand and across
                other metrics. It also checks if matching domains are available
                and estimates the price.
              </Typography>
              <Typography component="li" sx={styles.listItem}>
                <strong>Full Report</strong>: Performs all the above modes in
                one go.
              </Typography>
              <Box component="li" sx={styles.warningContainer}>
                <WarningIcon sx={styles.warningIcon} />
                <Typography sx={styles.paragraph}>
                  This is a tool for research only. While it has a comprehensive
                  set of trademark data, it doesn't always have the most updated
                  trademark data. Trademark data is very dynamic, and we try to
                  update daily, but that is not guaranteed. The tool also does
                  not provide legal advice and the product owners are not liable
                  for any legal issues due to trademark violations. Once you
                  have several great candidate names, please perform final
                  checks directly with domain providers and the{" "}
                  <Link
                    href="https://tmsearch.uspto.gov/search/search-information"
                    target="_blank"
                    rel="noopener"
                    sx={styles.link}
                  >
                    USPTO trademark database
                  </Link>
                  {"."} If you are considering trademarking the a name, best
                  practice is to consult a trademark attorney.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Typography variant="h5" gutterBottom sx={styles.subtitle}>
          🧭 Report Types and Run Instructions
        </Typography>
        <Box>
          <Typography variant="h6" gutterBottom sx={styles.subtitle}>
            🔹 Name Ideation Report
          </Typography>
          <Typography variant="body1" gutterBottom sx={styles.paragraph}>
            Use this mode to generate creative and unique name ideas for your
            product or company.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={styles.boldSubtitle}>
            Steps:
          </Typography>
          <Box>
            <ul>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Product or Company Candidate Names (optional)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Enter existing name ideas (comma-separated or one at a
                      time)
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Press Enter after each one
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Use this to iterate and refine ideas from past runs
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Project Description (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Briefly describe your product, company, or concept
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Helps the AI generate names tailored to your idea
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Number of Names to Generate (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Choose how many new name ideas to generate
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Industry (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Enter the industry you're in (e.g., "Fintech,"
                      "Education," "Healthcare")
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      This helps tailor name ideas to your specific industry
                      context
                    </Typography>
                  </li>
                </ul>
              </li>
            </ul>
          </Box>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={styles.subtitle}>
            🔹 Trademark Report
          </Typography>
          <Typography variant="body1" gutterBottom sx={styles.paragraph}>
            Use this mode to brainstorm names and check for potential trademark
            conflicts.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={styles.boldSubtitle}>
            Steps:
          </Typography>
          <Box>
            <ul>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Product or Company Candidate Names (optional)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Enter existing name ideas (comma-separated or one at a
                      time)
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Press Enter after each one
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Use this to iterate and refine ideas from past runs
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Project Description (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Briefly describe your product, company, or concept
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Helps the AI generate names tailored to your idea
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Number of Names to Generate (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Choose how many new name ideas to generate
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Industry (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Enter the industry you're in (e.g., "Fintech,"
                      "Education," "Healthcare")
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      This helps tailor name ideas and run more relevant
                      trademark checks in the appropriate trademark classes
                    </Typography>
                  </li>
                </ul>
              </li>
            </ul>
          </Box>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={styles.subtitle}>
            🔹 Domain Report
          </Typography>
          <Typography variant="body1" gutterBottom sx={styles.paragraph}>
            Use this mode to generate domain name ideas, check how well domain
            names score on SEO, clarity, brand and other metrics, and check if
            matching domains are available.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={styles.boldSubtitle}>
            Steps:
          </Typography>
          <Box>
            <ul>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Domain Name Candidates (optional)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Enter any name ideas you want to check or iterate on
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Press Enter to add each name
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Project Description (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Provide a brief explanation of your product or business
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Number of Domain Names to Generate (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Choose how many name ideas you want
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Preferred TLDs (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Select one or more top-level domains (TLDs) such as .com,
                      .net, .org, etc
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Stick with TLDs your audience is most familiar with — .com
                      is usually the safest choice
                    </Typography>
                  </li>
                </ul>
              </li>
            </ul>
          </Box>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={styles.subtitle}>
            🔹 Full Report
          </Typography>
          <Typography variant="body1" gutterBottom sx={styles.paragraph}>
            The full report first generates new names, then performs trademark
            search and domain search. The final report is a comprehensive
            analysis of brand name candidates, and analysis of brand names,
            trademark matches and domain name availability.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={styles.boldSubtitle}>
            Steps:
          </Typography>
          <Box>
            <ul>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Candidate Names (optional)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Add any name ideas you want to explore further
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Press Enter to submit each one
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Project Description (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Describe the product, service, or company
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Number of Candidate Names to Generate (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Select how many name ideas you want
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Industry (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Define the industry or category for trademark relevance
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="subtitle1" sx={styles.boldSubtitle}>
                  Preferred TLDs (required)
                </Typography>
                <ul>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Pick which domain extensions to check (e.g., .com, .org,
                      .ai)
                    </Typography>
                  </li>
                </ul>
              </li>
            </ul>
          </Box>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={styles.subtitle}>
            ✅ Submit, Review and Iterate
          </Typography>
          <Box>
            <ul>
              <li>
                <Typography sx={styles.paragraph}>
                  Click "Submit" to generate your report
                </Typography>
              </li>
              <li>
                <Typography sx={styles.paragraph}>
                  Results typically take about 60 seconds (still working on
                  latency)
                </Typography>
              </li>
              <li>
                <Typography sx={styles.paragraph}>
                  Review trademark and domain status of each name
                </Typography>
              </li>
              <li>
                <Typography sx={styles.paragraph}>
                  <strong>Iterate </strong>
                  by re-adding your favorite names in the next run: You can then
                  iterate in the following ways:
                </Typography>
                <ol>
                  <li>
                    <Typography sx={styles.paragraph}>
                      When you click the "Submit" button, the tool will generate
                      new name suggestions.
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      Next to any generated name, check the checkbox. Clicking
                      the checkbox will add the name back to your name candidate
                      inputs.
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      In your name candidate inputs, you can continue to add any
                      other candidates.
                    </Typography>
                  </li>
                  <li>
                    <Typography sx={styles.paragraph}>
                      You can then click submit again to generate more name that
                      are influenced by your previous inputs and new name
                      candidates. This is how you can guide the AI to generate
                      names that are more aligned with your brand and converge
                      on your creative direction.
                    </Typography>
                  </li>
                </ol>
              </li>
            </ul>
          </Box>
        </Box>

        <Box>
          <Typography variant="h5" gutterBottom sx={styles.subtitle}>
            Top Menu Bar
          </Typography>
          <Box>
            <ul>
              <li>
                <Typography sx={styles.paragraph}>
                  Documentation: View the documentation for the app
                </Typography>
              </li>
              <li>
                <Typography sx={styles.paragraph}>
                  Credit Status: View your credit status and purchase more
                  credits
                </Typography>
              </li>
              <li>
                <Typography sx={styles.paragraph}>
                  Notification: Check for notifications from the app
                </Typography>
              </li>
              <li>
                <Typography sx={styles.paragraph}>
                  History: View your research history and go back to any
                  previous report.
                </Typography>
              </li>
              <li>
                <Typography sx={styles.paragraph}>
                  User: View your user information and settings. You can also
                  logout from this menu.
                </Typography>
              </li>
            </ul>
          </Box>
        </Box>

        <Box>
          <Typography variant="h5" gutterBottom sx={styles.subtitle}>
            Upcoming Features
          </Typography>
          <Typography sx={styles.paragraph}>
            We are constantly working on improving the app. If you have any
            feature requests please use the Feedback button{" "}
            <FeedbackIcon sx={styles.feedbackIcon} /> on the bottom right of the
            screen to send us a message. We read all the feedback. Here are some
            of the features we are working on:
          </Typography>
          <Box>
            <ul>
              <li>
                <Typography sx={styles.paragraph}>
                  Add brand image and compare to existing brands
                </Typography>
              </li>
              <li>
                <Typography sx={styles.paragraph}>
                  Add more fields to trademark scores for a more comprehensive
                  risk assessment.
                </Typography>
              </li>
              <li>
                <Typography sx={styles.paragraph}>
                  Improve domain name generation, scoring, and availability
                  checks.
                </Typography>
              </li>
              <li>
                <Typography sx={styles.paragraph}>
                  Allow user to go beyond the 20 names limit.
                </Typography>
              </li>
              <li>
                <Typography sx={styles.paragraph}>
                  Add more fields to the full research report for a more
                  comprehensive analysis.
                </Typography>
              </li>
            </ul>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TutorialContent;
